import { Button, HTMLTable, Icon, Intent, Spinner, SpinnerSize } from "@blueprintjs/core"
import { useNavigate } from "react-router-dom"

import InfiniteScroll from "react-infinite-scroll-component"
import { FlexContainer, FlexContainerAndItem } from "../../../../styled/flexbox.styled"
import { ToastError } from "../../../components"
import { SpinnerContainer } from "../../../components/WithLoading/WithLoading.styled"
import useInfiniteDatasets from "../hooks/useInfiniteDatasets"
import { TableContainer } from "./DatasetTable.styled"
import { DatasetFilters, DatasetSource } from "./types"
import { useState } from "react"

interface IDatasetTableProps {
  filters: DatasetFilters
}

const DatasetTable = ({ filters }: IDatasetTableProps) => {
  const [copiedDatasetId, setCopiedDatasetId] = useState<number | null>(null)

  const { datasets, fetchNextPage, hasNextPage, isFetching, error } = useInfiniteDatasets({
    withPaginationParam: true,
    ...filters,
  })

  const navigate = useNavigate()

  const handleCopyCode = (dataset: any) => {
    const code = dataset.bloomberg_code || dataset.scient_code || "-"
    navigator.clipboard.writeText(code)
    setCopiedDatasetId(dataset.id)

    // Reset state after 1.5 seconds
    setTimeout(() => {
      setCopiedDatasetId(null)
    }, 1500)
  }

  if (!datasets || (isFetching && !datasets.length)) {
    return (
      <SpinnerContainer justifyContent="center" alignItems="center" flexGrow={1}>
        <Spinner intent={Intent.PRIMARY} size={SpinnerSize.STANDARD} />
      </SpinnerContainer>
    )
  }

  return (
    <TableContainer flexDirection="column" id="table-container">
      <InfiniteScroll
        dataLength={datasets.length}
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        loader={<Spinner intent={Intent.SUCCESS} size={SpinnerSize.SMALL} />}
        scrollableTarget="table-container"
        style={{ overflow: "visible" }}
      >
        <HTMLTable bordered striped>
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Internal</th>
              <th>Future release date</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {datasets.map(dataset => (
              <tr key={dataset.id}>
                <td>{dataset.long_name ? dataset.long_name : dataset.name}</td>
                <td>
                  <FlexContainer alignItems="center">
                    {dataset.bloomberg_code || dataset.scient_code || "-"}
                    <FlexContainerAndItem flexGrow={1} justifyContent="flex-end">
                      <Button
                        icon={copiedDatasetId === dataset.id ? "tick" : "clipboard"}
                        intent={copiedDatasetId === dataset.id ? Intent.SUCCESS : Intent.NONE}
                        minimal
                        onClick={() => handleCopyCode(dataset)}
                      />
                    </FlexContainerAndItem>
                  </FlexContainer>
                </td>
                <td className="center-icon">
                  {dataset.source === DatasetSource.INTERNAL && <Icon icon="tick" />}
                </td>
                <td>
                  {dataset.future_release_date
                    ? new Date(dataset.future_release_date).toLocaleString("en-GB", {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      })
                    : "-"}
                </td>
                <td>
                  <Button
                    icon="edit"
                    minimal
                    onClick={() => navigate(`/config/datasets/${dataset.id}`)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </HTMLTable>
      </InfiniteScroll>
      <ToastError
        error={error}
        errorMessage="Error loading datasets, please try to refresh the page."
      />
    </TableContainer>
  )
}
export default DatasetTable
